import React from 'react';
import { Edit, Trash2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { formatDate } from '../../../utils/dateUtils';

interface BlogPostListProps {
  posts: any[];
  isLoading: boolean;
  onEdit: (post: any) => void;
  onDelete: (postId: string) => void;
}

export function BlogPostList({ posts, isLoading, onEdit, onDelete }: BlogPostListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-black">No blog posts found. Create your first post to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <div key={post.id} className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-medium text-black">{post.title}</h3>
              <div className="flex items-center space-x-4 mt-1">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  post.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {post.status}
                </span>
                {post.published_at && (
                  <span className="text-sm text-gray-500">
                    Published {formatDate(post.published_at)}
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                {post.excerpt || 'No excerpt available'}
              </p>
            </div>
            <div className="flex space-x-2">
              <Link
                to={`/blog/${post.id}`}
                target="_blank"
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="View Post"
              >
                <Eye className="h-4 w-4" />
              </Link>
              <button
                onClick={() => onEdit(post)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Edit Post"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this post?')) {
                    onDelete(post.id);
                  }
                }}
                className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"
                title="Delete Post"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}